import React, {useState, useEffect} from 'react';
import './Home.css';
import axios from 'axios';
import { domain, URL_WS_URL } from '../variables';
import BoxChamp from '../components/BoxChamp';
import BoxPlayOff from '../components/BoxPlayOff';
import BoxLivescoring from '../components/BoxLivescoring';
import Carrousel from '../components/Carrousel';
import { findMatchVideo, VIDEO_CHAMPIONNATS } from '../videoMatching';
import { useTranslation } from '../i18n';

// Vidéo mise en avant sur l'accueil
const YOUTUBE_VIDEO_ID = 'DiRjuLAOWEU';

function Home() {
    const { t, locale } = useTranslation();
    const [actus, setActus] = useState([]);
    const [matchsLivescoring, setMatchsLivescoring] = useState(null);
    // Le lecteur YouTube (plusieurs centaines de Ko) n'est chargé qu'au clic sur la vignette
    const [videoActive, setVideoActive] = useState(false);
    const [currentItemCaroussel, setCurrentItemCaroussel] = useState(0);
    const LENGTH_CAROUSSEL = 10;

    // Activer l'affichage des playoff / playdown sur le site.
    const playOffDownVisble = false;
    const playOffProAVisible = false;

    // Vidéos YouTube des rencontres du jour : { live, upcoming, completed }
    const [videos, setVideos] = useState(null);

    useEffect(() => {
        getMatchLivescoring()
    }, [])

    // Tant qu'une rencontre est en direct, on rafraîchit les scores toutes les 30 s
    useEffect(() => {
        const enDirect = matchsDuJour.some(({match}) => match.status === 'en_cours');
        if (!enDirect) return;
        const timer = setTimeout(getMatchLivescoring, 30000);
        return () => clearTimeout(timer);
    }, [matchsLivescoring])

    // Chargées seulement s'il y a aujourd'hui une rencontre d'un championnat diffusé
    useEffect(() => {
        const hasVideoMatch = matchsDuJour.some(({championnat}) => VIDEO_CHAMPIONNATS.includes(championnat));
        if (hasVideoMatch && videos === null) {
            axios.post(domain + '/server/getVideos.php', {})
                .then((res) => setVideos(res.data && typeof res.data === 'object' ? res.data : {}))
                .catch(() => setVideos({}));
        }
    }, [matchsLivescoring])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (actus.length > 0) {
                if (currentItemCaroussel >= actus.length-1) {
                    setCurrentItemCaroussel(0);
                }
                else {
                    setCurrentItemCaroussel(currentItemCaroussel+1)
                }
            }
        }, 3000)

        if (actus.length === 0) {
            getActus();
        }

        return () => clearTimeout(timeout);
    }, [currentItemCaroussel, actus.length])

    const getActus = async () => {
        const link = domain + '/server/getActualites.php';
        await axios.post(
            link, 
            {
                tag: 4,
                offset: 0,
                limit: LENGTH_CAROUSSEL,
            }
        ).then(res => {
            const data = res.data;
            setActus(data);
        })
    }

    const getMatchLivescoring = async () => {
        const link = URL_WS_URL + '/livescore/directs_with_dates';
        await axios.get(
            link, 
            {
                params: {
                    // date: "19/09/2023"
                    // on récupère les matchs d'aujourd'hui
                    date: new Date().toLocaleDateString('fr-FR')
                }
            }
        ).then(res => {
            const data = res.data;
            setMatchsLivescoring(data);
        })
    }

    // Fonction pour récupérer le nom du championnat grâce au TAG livescoring
    const getChampionnatByTags = (tags) => {
        switch (tags[0]) {
            case "PRO A":
                if (tags[1] === "MESSIEURS") {
                    return 'proam';
                } 
                else {
                    return 'proad';
                }
            case "PRO B":
                if (tags[1] === "MESSIEURS") {
                    return 'probm';
                }
                else {
                    return 'probd';
                }
            case "PREPROD":
                return 'preprod';
            default:
                return null;
        }
    }

    // Matchs du jour (date d'index 0 du livescoring), à plat avec leur championnat
    const matchsDuJour = (matchsLivescoring?.dates?.find(item => item.index === 0)?.groups || [])
        .flatMap((group) => group.matchs.map((match) => ({match, championnat: getChampionnatByTags(group.tags)})));
    // Rencontres des jours passés (index < 0) et du jour, indexées par renc_id : score en
    // direct dans les cartes championnat, et résultat d'un match terminé tant que la FFTT
    // ne l'a pas encore publié (le lendemain d'une rencontre du soir par exemple)
    const livescores = Object.fromEntries((matchsLivescoring?.dates || [])
        .filter((item) => item.index <= 0)
        .flatMap((item) => item.groups || [])
        .flatMap((group) => group.matchs)
        .map((match) => [String(match.renc_id), match]));
    // Date du jour au format SPID ("25/09/2026") : rapprochement des vidéos de phases finales
    const todayFr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

    const goToActus = (actu) => {
        window.open(actu, "_blank");
    }

    // Date de l'actu formatee comme sur fftt.com (ex : "7 juillet 2026")
    const formatActuDate = (raw) => {
        if (!raw) return null;
        const date = new Date(raw.replace(' ', 'T'));
        if (isNaN(date.getTime())) return null;
        return date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
    }

    // Tag de l'actu, deduit de la categorie presente dans le slug (ex : "PRO A PRO B")
    const getActuTag = (slug) => {
        if (!slug) return null;
        try {
            const parts = new URL(slug).pathname.split('/').filter(Boolean);
            const index = parts.indexOf('actualites');
            const category = index >= 0 ? parts[index + 1] : null;
            if (!category) return null;
            return category.replace(/-/g, ' ').toUpperCase();
        } catch (e) {
            return null;
        }
    }

    return ( 
        <div className="home_page">
            <div className="caroussel">
                {
                    actus.map((item, index) => {
                        return (
                            <div 
                                key={index} 
                                className="caroussel_item"
                                style={{transform: `translate(-${currentItemCaroussel * 100}%)`}}
                            >
                                <img
                                    src={item.news_medias_picture}
                                    alt=""
                                    // seule la 1re actu est visible au chargement : les suivantes sont différées
                                    loading={index === 0 ? 'eager' : 'lazy'}
                                    fetchpriority={index === 0 ? 'high' : 'low'}
                                    decoding="async"
                                    style={{cursor: 'pointer'}}
                                    onClick={()=>goToActus(item.pages_fr_slug)}
                                />
                                <div className="caroussel_content">
                                    {
                                        getActuTag(item.pages_fr_slug) &&
                                        <span className="caroussel_tag">{getActuTag(item.pages_fr_slug)}</span>
                                    }
                                    <div className="line" style={{width: 100}}></div>
                                    {
                                        formatActuDate(item.news_date) &&
                                        <span className="caroussel_date">{formatActuDate(item.news_date)}</span>
                                    }
                                    <span className="caroussel_title">
                                        {item.news_title}
                                    </span>
                                </div>
                            </div>
                        )
                    })
                }
                <div className="caroussel_point_container">
                    {
                        actus.map((item, index) => {
                            return (
                                <div 
                                    key={index} 
                                    className={currentItemCaroussel === index ? "caroussel_point active" : "caroussel_point"}
                                    onClick={()=>setCurrentItemCaroussel(index)}
                                >
                                </div>
                            )
                        })
                    }
                </div>
            </div>
            {/* Compartiment pour afficher les matchs du livescoring (masqué s'il n'y a aucun match aujourd'hui) */}
            {
                matchsDuJour.length > 0 &&
                <section className="home_section home_container under_caroussel">
                    <h2 className="home_section_title">
                        {t('common.today')} <span className="home_section_count">{matchsDuJour.length}</span>
                    </h2>
                    <Carrousel showPagination interval={3000} automatic>
                        {
                            matchsDuJour.map(({match, championnat}, index) => (
                                <BoxLivescoring
                                    key={index}
                                    match={match}
                                    championnat={championnat}
                                    video={findMatchVideo(videos, {
                                        championnat,
                                        day: parseInt(match.tour, 10),
                                        teamA: match.equa,
                                        teamB: match.equb,
                                        date: todayFr,
                                    })}
                                />
                            ))
                        }
                    </Carrousel>
                </section>
            }

            <section className="home_section home_container">
            <div className="home_content">
                {
                    (playOffDownVisble === true) &&
                    <>
                        {
                            (playOffProAVisible === true) &&
                            <>
                                <BoxPlayOff championnat='proam' />
                            </>  
                        }
                        {
                            (playOffProAVisible === false) &&
                            <>
                                <BoxChamp championnat='proam' livescores={livescores} />
                            </>
                        }
                        <BoxPlayOff championnat='proad' />
                        <BoxPlayOff championnat='probm' />
                        <BoxChamp championnat='probd' livescores={livescores} />  
                    </>
                }
                {
                    (playOffDownVisble === false) &&
                    <>
                        <BoxChamp championnat='proam' livescores={livescores} />
                        <BoxChamp championnat='proad' livescores={livescores} />
                        <BoxChamp championnat='probm' livescores={livescores} />
                        <BoxChamp championnat='probd' livescores={livescores} />
                    </>
                }
            </div>
            </section>

            <section className="home_section home_container home_section_video">
                {
                    (videoActive &&
                    <iframe
                        title={t('home.videoTitle')}
                        className="home_video"
                        src={`https://www.youtube-nocookie.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1`}
                        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                        allowFullScreen
                    />)
                    ||
                    <button type="button" className="home_video home_video_facade" onClick={() => setVideoActive(true)} aria-label={t('home.playVideo')}>
                        <img
                            src={`https://i.ytimg.com/vi/${YOUTUBE_VIDEO_ID}/hqdefault.jpg`}
                            alt=""
                            loading="lazy"
                            decoding="async"
                        />
                        <span className="home_video_play" aria-hidden="true"></span>
                    </button>
                }
            </section>

            {/* <div className="home_maintenance">
                <h1>Site en maintenance</h1>
                <h3>Le site subit des modifications en prévision de la prochaine saison 2023-2024</h3>
            </div> */}
        </div>
    );
}

export default Home;